/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { produce } from 'immer';

/**
 * Call status type for tracking GraphQL query states
 */
export type HQVCallStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * Datalayer call status type
 */
export type DatalayerCallStatus = 'pending' | 'called';

/**
 * Interface for HQV Datalayer Tracking store state
 */
export interface HQVDatalayerTrackingState {
  propertyInfoCallStatus: HQVCallStatus;
  photogalleryCallStatus: HQVCallStatus;
  datalayerCallStatus: DatalayerCallStatus;
}

/**
 * Interface for HQV Datalayer Tracking store actions
 */
export interface HQVDatalayerTrackingActions {
  setPropertyInfoCallStatus: (status: HQVCallStatus) => void;
  setPhotogalleryCallStatus: (status: HQVCallStatus) => void;
  setDatalayerCallStatus: (status: DatalayerCallStatus) => void;
  resetStore: () => void;
}

/**
 * Combined store type
 */
export type HQVDatalayerTrackingStore = HQVDatalayerTrackingState & HQVDatalayerTrackingActions;

/**
 * Initial state for the store
 */
const initialState: HQVDatalayerTrackingState = {
  propertyInfoCallStatus: 'idle',
  photogalleryCallStatus: 'idle',
  datalayerCallStatus: 'pending',
};

/**
 * HQV Datalayer Tracking Zustand store creator
 * Tracks the completion/failure status of phoenixShopHQVPropertyInfoCall and phoenixShopHQVPhotogalleryCall
 * to conditionally trigger datalayer calls only after both complete
 */
const hqvDatalayerTrackingStoreCreator: StateCreator<HQVDatalayerTrackingStore> = set => {
  return {
    ...initialState,

    setPropertyInfoCallStatus: (status: HQVCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.propertyInfoCallStatus = status;
        })
      );
    },

    setPhotogalleryCallStatus: (status: HQVCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.photogalleryCallStatus = status;
        })
      );
    },

    setDatalayerCallStatus: (status: DatalayerCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.datalayerCallStatus = status;
        })
      );
    },

    resetStore: () => {
      set({ ...initialState });
    },
  };
};

/**
 * Create the HQV Datalayer Tracking store using the existing createAppStore pattern
 */
export const useHQVDatalayerTrackingStore = createAppStore(hqvDatalayerTrackingStoreCreator, {
  usePersistentStore: false,
});
